/**
 * Course Catalog Page - Browse and filter courses
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Navigation } from '../../components/layout/Navigation';
import { Footer } from '../../components/layout/Footer';
import { Container } from '../../components/ui/Container';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { CourseCard } from '../../components/course/CourseCard';
import { Spinner } from '../../components/ui/Spinner';
import type { FilterParams } from '../../types/common';
import type { Course } from '../../types/models';
import { debounce } from '../../utils/fp-utils';
import { getPage } from '../../service/app/app.service';
import { getBillingState } from '../../services/billing/local-billing';
import './courses.css';

type BootcampApiItem = Readonly<{
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  thumbnail: string;
  trailerUrl?: string;
  paymentLink?: string;
  payment_link?: string;
  stripePaymentLink?: string;
  stripe_payment_link?: string;
  checkoutUrl?: string;
  checkout_url?: string;
  instructor?: Readonly<{
    profileImg?: string;
    name?: string;
  }>;
  level?: string;
  language?: string;
  topics?: ReadonlyArray<string>;
  duration?: number;
  lessonsCount?: number;
  studentsCount?: number;
  rating?: number;
  reviewsCount?: number;
  isPublished?: boolean;
  createdAt?: string;
  updatedAt?: string;
}>;

type BootcampPageData = Readonly<{
  bootcamps?: ReadonlyArray<BootcampApiItem>;
  data?: Readonly<{
    bootcamps?: ReadonlyArray<BootcampApiItem>;
  }>;
}>;

type FilterKey = 'search' | 'level' | 'language' | 'topic';
type VisiblePage = number | 'ellipsis-left' | 'ellipsis-right';

const vertexShaderSource = `
  attribute vec2 aPosition;
  varying vec2 vUv;
  void main() {
    vUv = (aPosition + 1.0) * 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

const fragmentShaderSource = `
  precision mediump float;
  uniform float uTime;
  uniform vec2 uResolution;
  varying vec2 vUv;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = (uv - 0.5) * vec2(uResolution.x / max(uResolution.y, 1.0), 1.0);
    float t = uTime * 0.24;

    float rings = sin(length(p) * 18.0 - t * 6.0);
    float grid = sin((p.x + t) * 20.0) * sin((p.y - t * 0.55) * 20.0);
    float clouds = noise((p * 3.2) + vec2(t * 0.8, -t * 0.6));
    float centerPulse = smoothstep(1.0, 0.0, length(p * vec2(1.2, 1.0)));

    vec3 base = vec3(0.03, 0.07, 0.14);
    vec3 cyan = vec3(0.50, 0.96, 1.0);
    vec3 amber = vec3(0.96, 0.80, 0.36);
    vec3 color = base;

    color += cyan * smoothstep(-0.35, 0.85, grid + clouds * 0.35) * 0.32;
    color += amber * smoothstep(-0.4, 0.9, rings + clouds * 0.5) * 0.28;
    color += vec3(0.08, 0.17, 0.36) * centerPulse * 0.6;
    color += vec3(noise((uv * 9.0) + t) * 0.03);

    gl_FragColor = vec4(color, 0.84);
  }
`;

const createShader = (
  gl: WebGLRenderingContext,
  shaderType: number,
  source: string
): WebGLShader | null => {
  const shader = gl.createShader(shaderType);
  if (!shader) {
    return null;
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

const createProgram = (
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram | null => {
  const program = gl.createProgram();
  if (!program) {
    return null;
  }
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
};

const CourseFieldCanvas: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    if (!gl) {
      return undefined;
    }

    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) {
      return undefined;
    }

    const program = createProgram(gl, vertexShader, fragmentShader);
    if (!program) {
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      return undefined;
    }

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    const buffer = gl.createBuffer();
    if (!buffer) {
      gl.deleteProgram(program);
      return undefined;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const positionLocation = gl.getAttribLocation(program, 'aPosition');
    const timeLocation = gl.getUniformLocation(program, 'uTime');
    const resolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    let frame = 0;
    const start = performance.now();

    const render = (now: number) => {
      resize();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      if (timeLocation) {
        gl.uniform1f(timeLocation, (now - start) / 1000);
      }
      if (resolutionLocation) {
        gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      }

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };

    const loop = (now: number) => {
      render(now);
      frame = window.requestAnimationFrame(loop);
    };

    resize();
    if (reduceMotionQuery.matches) {
      render(start);
    } else {
      frame = window.requestAnimationFrame(loop);
    }

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(frame);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, []);

  return <canvas ref={canvasRef} className="h-full w-full" aria-hidden="true" />;
};

const isCourseLevel = (value?: string): value is Course['level'] =>
  value === 'beginner' || value === 'intermediate' || value === 'advanced' || value === 'all-levels';

const toCourse = (item: BootcampApiItem): Course => {
  const instructorName = item.instructor?.name || 'Bootcamp Instructor';
  const instructorSlug = instructorName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const now = new Date().toISOString();

  const paymentLink =
    item.paymentLink ||
    item.payment_link ||
    item.stripePaymentLink ||
    item.stripe_payment_link ||
    item.checkoutUrl ||
    item.checkout_url;

  return {
    id: item.id,
    title: item.title,
    description: item.description,
    shortDescription: item.shortDescription || item.description,
    thumbnail: item.thumbnail,
    trailerUrl: item.trailerUrl,
    paymentLink,
    instructor: {
      id: `inst-${instructorSlug}`,
      name: instructorName,
      title: 'Bootcamp Instructor',
      bio: `${instructorName} leads this coding bootcamp track.`,
      avatar:
        item.instructor?.profileImg ||
        'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200',
      expertise: item.topics || [],
      coursesCount: 1,
    },
    level: isCourseLevel(item.level) ? item.level : 'beginner',
    language: item.language || 'javascript',
    topics: item.topics || [],
    duration: item.duration || 0,
    lessonsCount: item.lessonsCount || 0,
    studentsCount: item.studentsCount || 0,
    rating: item.rating || 0,
    reviewsCount: item.reviewsCount || 0,
    isPublished: item.isPublished ?? true,
    createdAt: item.createdAt || now,
    updatedAt: item.updatedAt || now,
  };
};

const formatFilterLabel = (value: string): string =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getVisiblePages = (currentPage: number, totalPages: number): ReadonlyArray<VisiblePage> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: VisiblePage[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push('ellipsis-left');
  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }
  if (end < totalPages - 1) pages.push('ellipsis-right');

  pages.push(totalPages);
  return pages;
};

const FilterSidebar: React.FC<{
  filters: FilterParams;
  onFilterChange: (key: FilterKey, value: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}> = ({ filters, onFilterChange, onClearFilters, hasActiveFilters }) => {
  return (
    <aside className="courses-panel sticky top-24 rounded-2xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="courses-display text-lg font-semibold text-white">Refine Results</h3>
        <button
          type="button"
          onClick={onClearFilters}
          disabled={!hasActiveFilters}
          className="text-xs uppercase tracking-wide text-amber-200 disabled:opacity-40"
        >
          Clear
        </button>
      </div>

      <div className="mb-5">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
          Level
        </label>
        <select
          value={filters.level || ''}
          onChange={(event) => onFilterChange('level', event.target.value)}
          className="course-filter-select"
        >
          <option value="">All Levels</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
          <option value="all-levels">All Levels</option>
        </select>
      </div>

      <div className="mb-5">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
          Language
        </label>
        <select
          value={filters.language || ''}
          onChange={(event) => onFilterChange('language', event.target.value)}
          className="course-filter-select"
        >
          <option value="">All Languages</option>
          <option value="javascript">JavaScript</option>
          <option value="typescript">TypeScript</option>
          <option value="python">Python</option>
          <option value="java">Java</option>
          <option value="go">Go</option>
          <option value="rust">Rust</option>
          <option value="react">React</option>
          <option value="nodejs">Node.js</option>
          <option value="kotlin">Kotlin</option>
          <option value="swift">Swift</option>
        </select>
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
          Topic
        </label>
        <select
          value={filters.topic || ''}
          onChange={(event) => onFilterChange('topic', event.target.value)}
          className="course-filter-select"
        >
          <option value="">All Topics</option>
          <option value="frontend">Frontend</option>
          <option value="backend">Backend</option>
          <option value="api">API Design</option>
          <option value="testing">Testing</option>
          <option value="cloud">Cloud</option>
          <option value="mobile">Mobile</option>
          <option value="architecture">Architecture</option>
        </select>
      </div>
    </aside>
  );
};

export const CourseCatalogPage: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  const isMyCoursesPage = location.pathname === '/my-courses';
  const purchasedCourseIds = getBillingState().purchasedCourseIds;

  React.useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  const filters: FilterParams = useMemo(
    () => ({
      search: searchParams.get('search') || undefined,
      level: searchParams.get('level') || undefined,
      language: searchParams.get('language') || undefined,
      topic: searchParams.get('topic') || undefined,
      page: Number(searchParams.get('page')) || 1,
      limit: 12,
    }),
    [searchParams]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['bootcamp-page'],
    queryFn: async () => {
      const result = await getPage<BootcampPageData>('bootcamp');
      if (result.error || !result.data) {
        throw new Error(
          typeof result.error === 'string' ? result.error : 'Failed to load bootcamp data'
        );
      }

      const raw = result.data.bootcamps || result.data.data?.bootcamps || [];
      return raw.map(toCourse);
    },
    staleTime: 5 * 60 * 1000,
  });

  const filteredCourses = useMemo(() => {
    if (!data) return [];

    const sourceCourses = isMyCoursesPage
      ? data.filter((course) => purchasedCourseIds.includes(course.id.trim().toLowerCase()))
      : data;

    return sourceCourses.filter((course) => {
      const searchMatch =
        !filters.search ||
        course.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        course.shortDescription.toLowerCase().includes(filters.search.toLowerCase()) ||
        course.topics.some((topic) => topic.toLowerCase().includes(filters.search!.toLowerCase()));

      const levelMatch = !filters.level || course.level === filters.level;
      const languageMatch = !filters.language || course.language === filters.language;
      const topicMatch =
        !filters.topic ||
        course.topics.some((topic) => topic.toLowerCase().includes(filters.topic!.toLowerCase()));

      return searchMatch && levelMatch && languageMatch && topicMatch;
    });
  }, [data, filters, isMyCoursesPage, purchasedCourseIds]);

  const currentPage = filters.page || 1;
  const perPage = filters.limit || 12;
  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / perPage));
  const start = (currentPage - 1) * perPage;
  const pagedCourses = filteredCourses.slice(start, start + perPage);

  const stats = useMemo(() => {
    if (!filteredCourses.length) {
      return [
        { label: 'Visible Courses', value: '0' },
        { label: 'Instructors', value: '0' },
        { label: 'Avg Rating', value: '0.0' },
        { label: 'Total Lessons', value: '0' },
      ] as const;
    }

    const instructors = new Set(filteredCourses.map((course) => course.instructor.id)).size;
    const avgRating =
      filteredCourses.reduce((sum, course) => sum + course.rating, 0) / filteredCourses.length;
    const totalLessons = filteredCourses.reduce((sum, course) => sum + course.lessonsCount, 0);

    return [
      { label: 'Visible Courses', value: String(filteredCourses.length) },
      { label: 'Instructors', value: String(instructors) },
      { label: 'Avg Rating', value: avgRating.toFixed(1) },
      { label: 'Total Lessons', value: String(totalLessons) },
    ] as const;
  }, [filteredCourses]);

  const activeFilters = useMemo(
    () =>
      [
        filters.search ? { key: 'search', label: `Search: ${filters.search}` } : null,
        filters.level ? { key: 'level', label: `Level: ${formatFilterLabel(filters.level)}` } : null,
        filters.language
          ? { key: 'language', label: `Language: ${formatFilterLabel(filters.language)}` }
          : null,
        filters.topic ? { key: 'topic', label: `Topic: ${formatFilterLabel(filters.topic)}` } : null,
      ].filter(Boolean) as ReadonlyArray<Readonly<{ key: FilterKey; label: string }>>,
    [filters]
  );

  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
          newParams.set('search', value);
        } else {
          newParams.delete('search');
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
      }, 350),
    [searchParams, setSearchParams]
  );

  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    handleSearchChange(value);
  };

  const handleFilterChange = (key: FilterKey, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleRemoveFilter = (key: FilterKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    newParams.set('page', '1');
    setSearchParams(newParams);
    if (key === 'search') {
      setSearchQuery('');
    }
  };

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams());
    setSearchQuery('');
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(page));
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="courses-page min-h-screen text-white">
      <Navigation />

      <main className="relative overflow-hidden pb-20 pt-28 md:pt-32">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[560px] opacity-95">
          <CourseFieldCanvas />
        </div>

        <Container>
          <section className="courses-panel rounded-[1.9rem] p-7 md:p-10">
            <p className="mb-3 inline-flex rounded-full border border-cyan-200/65 bg-cyan-100/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">
              Course Library
            </p>
            <h1 className="courses-display text-5xl font-bold md:text-6xl">
              {isMyCoursesPage ? 'Your Learning Dashboard' : 'Discover Your Next Build Challenge'}
            </h1>
            <p className="mt-4 max-w-4xl text-lg text-slate-100/88">
              {isMyCoursesPage
                ? 'These are courses you have purchased. Jump back into active lessons and keep your momentum.'
                : 'Browse curated engineering courses designed for production-ready skills, architecture decisions, and portfolio outcomes.'}
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/instructors">
                <Button variant="gold" size="md">
                  Meet Instructors
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" size="md">
                  How We Teach
                </Button>
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
              {stats.map((stat) => (
                <article key={stat.label} className="courses-pill rounded-xl p-4 text-center">
                  <p className="courses-display text-3xl font-bold text-white">{stat.value}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-200">{stat.label}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="courses-panel mt-8 rounded-2xl p-5">
            <Input
              type="search"
              placeholder="Search by title, topic, or focus area..."
              value={searchQuery}
              onChange={onSearchChange}
            />
            {activeFilters.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {activeFilters.map((filterItem) => (
                  <button
                    type="button"
                    key={filterItem.key}
                    onClick={() => handleRemoveFilter(filterItem.key)}
                    className="courses-pill rounded-full px-3 py-1 text-xs font-medium text-slate-100"
                  >
                    {filterItem.label} ×
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-4">
            <div className="lg:col-span-1">
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                hasActiveFilters={activeFilters.length > 0}
              />
            </div>

            <div className="lg:col-span-3">
              {isLoading ? (
                <div className="courses-panel flex justify-center rounded-2xl py-20">
                  <Spinner size="lg" />
                </div>
              ) : error ? (
                <div className="courses-panel rounded-2xl py-20 text-center">
                  <p className="mb-3 text-red-300">Failed to load courses.</p>
                  <p className="text-slate-300">{(error as Error).message}</p>
                </div>
              ) : pagedCourses.length === 0 ? (
                <div className="courses-panel rounded-2xl py-20 text-center">
                  <p className="text-lg text-slate-100">
                    {isMyCoursesPage ? 'No purchased courses yet.' : 'No courses match your filters.'}
                  </p>
                  <p className="mt-2 text-slate-300">
                    {isMyCoursesPage
                      ? 'Buy a course to see it here.'
                      : 'Try clearing filters or adjusting your search.'}
                  </p>
                  {!isMyCoursesPage && (
                    <div className="mt-5">
                      <Button variant="outline" size="sm" onClick={handleClearFilters}>
                        Reset Filters
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="mb-5 flex items-center justify-between">
                    <p className="text-sm text-slate-200">
                      Showing <span className="font-semibold text-white">{pagedCourses.length}</span> of{' '}
                      <span className="font-semibold text-white">{filteredCourses.length}</span> courses
                    </p>
                    <p className="text-xs uppercase tracking-[0.14em] text-cyan-100/80">
                      Page {currentPage} / {totalPages}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                    {pagedCourses.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        detailBasePath={isMyCoursesPage ? '/my-courses' : '/courses'}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="courses-pagination-btn px-3 py-2 text-sm"
                      >
                        Prev
                      </button>

                      {visiblePages.map((pageValue) =>
                        typeof pageValue === 'number' ? (
                          <button
                            key={pageValue}
                            type="button"
                            onClick={() => handlePageChange(pageValue)}
                            className={`courses-pagination-btn px-3 py-2 text-sm ${
                              pageValue === currentPage ? 'courses-pagination-active' : ''
                            }`}
                          >
                            {pageValue}
                          </button>
                        ) : (
                          <span key={pageValue} className="px-1 text-slate-300">
                            ...
                          </span>
                        )
                      )}

                      <button
                        type="button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="courses-pagination-btn px-3 py-2 text-sm"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </Container>
      </main>

      <Footer />
    </div>
  );
};
